import { Button, radius } from '@alison/ui';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { z } from 'zod';
import { ChannelManageDrawer } from '@/components/settings/notifications/ChannelManageDrawer.js';
import { EmailChannelCard } from '@/components/settings/notifications/EmailChannelCard.js';
import { channelDelivery } from '@/components/settings/notifications/notifications.data.js';
import type {
  CadenceId,
  ChannelId,
  EmailData,
  EventPrefs,
  RoleId,
  SlackData,
} from '@/components/settings/notifications/notifications.types.js';
import { PrefsGrid } from '@/components/settings/notifications/PrefsGrid.js';
import { QuietHours } from '@/components/settings/notifications/QuietHours.js';
import { SlackChannelCard } from '@/components/settings/notifications/SlackChannelCard.js';
import { SlackSetupDialog } from '@/components/settings/notifications/SlackSetupDialog.js';
import { SlackSetupSuccessToast } from '@/components/settings/notifications/SlackSetupSuccessToast.js';
import { useSlackSetupFlow } from '@/components/settings/notifications/useSlackSetupFlow.js';
import {
  useDisconnectSlack,
  useNotificationChannels,
  useNotificationPrefs,
  useSlackTest,
  useUpdateNotificationPrefs,
} from '../../../lib/notifications.hooks.js';
import { useTRPC } from '../../../lib/trpc.js';

const route = getRouteApi('/settings/notifications/');

const notificationsSearchSchema = z
  .object({
    connected: z.literal('slack').optional().catch(undefined),
    error: z.string().optional().catch(undefined),
  })
  .catch({});

const FALLBACK_SLACK: SlackData = {
  id: 'slack',
  status: 'disconnected',
  availability: 'available',
  workspace: '',
  channel: '',
  overrides: [],
};

const FALLBACK_EMAIL: EmailData = {
  id: 'email',
  status: 'disconnected',
  sender: '',
  recipients: [],
};

export function NotificationsPage(): ReactNode {
  const search = route.useSearch();
  const trpc = useTRPC();
  const meQuery = useQuery(trpc.me.get.queryOptions());
  const channelsQuery = useNotificationChannels();
  const refetchChannels = channelsQuery.refetch;
  const prefsQuery = useNotificationPrefs();
  const updateMutation = useUpdateNotificationPrefs();
  const disconnectSlack = useDisconnectSlack();
  const slackTest = useSlackTest();

  const [role, setRole] = useState<RoleId>('ua');
  const [manageChannel, setManageChannel] = useState<Exclude<ChannelId, 'inapp'> | null>(null);

  const slack: SlackData = channelsQuery.data?.slack ?? FALLBACK_SLACK;
  const email: EmailData = channelsQuery.data?.email ?? FALLBACK_EMAIL;
  const delivery = channelDelivery({ slack, email });
  // React Query owns the prefs; the optimistic write in
  // useUpdateNotificationPrefs keeps the cache authoritative between requests.
  const prefs = prefsQuery.data;
  const clientId = meQuery.data?.clientId;
  const canManage = meQuery.data?.canManageClient === true;
  const slackSetup = useSlackSetupFlow({
    search,
    canManage,
    isUserPending: meQuery.isPending,
    clientId,
    slack,
    refetchChannels,
  });

  if (prefsQuery.isPending || channelsQuery.isPending || meQuery.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (
    !prefs ||
    clientId === undefined ||
    prefsQuery.isError ||
    channelsQuery.isError ||
    meQuery.isError
  ) {
    return (
      <Stack spacing={2} sx={{ alignItems: 'center', py: 8 }}>
        <Alert severity="error">Couldn't load your notification settings.</Alert>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            void Promise.all([prefsQuery.refetch(), refetchChannels(), meQuery.refetch()]);
          }}
        >
          Retry
        </Button>
      </Stack>
    );
  }

  function handleToggle(roleId: RoleId, event: string, channel: ChannelId): void {
    if (!prefs || updateMutation.isPending) return;
    const rolePrefs: EventPrefs = prefs[roleId];
    const eventKey = event as keyof EventPrefs;
    updateMutation.mutate({
      role: roleId,
      prefs: {
        ...rolePrefs,
        [eventKey]: { ...rolePrefs[eventKey], [channel]: !rolePrefs[eventKey][channel] },
      },
    });
  }

  function handleCadenceChange(roleId: RoleId, event: string, cadence: CadenceId): void {
    if (!prefs || updateMutation.isPending) return;
    const rolePrefs: EventPrefs = prefs[roleId];
    const eventKey = event as keyof EventPrefs;
    updateMutation.mutate({
      role: roleId,
      prefs: { ...rolePrefs, [eventKey]: { ...rolePrefs[eventKey], cadence } },
    });
  }

  function handleDrawerToggle(event: string, channel: Exclude<ChannelId, 'inapp'>): void {
    handleToggle(role, event, channel);
  }

  function handleDrawerCadence(event: string, cadence: CadenceId): void {
    handleCadenceChange(role, event, cadence);
  }

  function disconnectSlackWorkspace(): void {
    slackSetup.setNotice(null);
    disconnectSlack.mutate(undefined, {
      onSuccess: () => {
        setManageChannel(null);
        slackSetup.setNotice({ kind: 'success', text: 'Slack disconnected' });
      },
      onError: () => slackSetup.setNotice({ kind: 'error', text: 'Could not disconnect Slack' }),
    });
  }

  function sendSlackTest(): void {
    slackSetup.setNotice(null);
    slackTest.mutate(undefined, {
      onSuccess: (result) =>
        slackSetup.setNotice(
          result.ok
            ? { kind: 'success', text: 'Test sent' }
            : { kind: 'error', text: 'Test message failed' },
        ),
      onError: () => slackSetup.setNotice({ kind: 'error', text: 'Test message failed' }),
    });
  }

  return (
    <Stack sx={{ width: '100%', gap: 6 }}>
      <Stack spacing={1}>
        <Typography component="h2" sx={{ fontSize: 18, lineHeight: 1.25, fontWeight: 700 }}>
          Notifications
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 400, color: 'text.secondary' }}>
          Choose where Alison reaches you and what you hear about — across Slack, email and in-app.
        </Typography>
      </Stack>

      <Stack sx={{ gap: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13 }}>
          Delivery channels
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          <SlackChannelCard
            data={slack}
            canManage={canManage}
            testPending={slackTest.isPending}
            onConnect={() => void slackSetup.startSlackLink()}
            onReconnect={() => void slackSetup.startSlackLink()}
            onConfigure={() => slackSetup.openSetup('configure')}
            onRefresh={() => void refetchChannels()}
            onManage={() => setManageChannel('slack')}
            onTest={sendSlackTest}
          />
          <EmailChannelCard
            data={email}
            canManage={canManage}
            onManage={() => setManageChannel('email')}
          />
        </Box>
        {slackSetup.notice &&
          manageChannel === null &&
          (slackSetup.notice.kind === 'error' ? (
            <Alert severity="error">{slackSetup.notice.text}</Alert>
          ) : (
            <Typography variant="body2" sx={{ color: 'success.main' }}>
              {slackSetup.notice.text}
            </Typography>
          ))}
      </Stack>

      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: `${radius.xl}px`,
          boxShadow: 1,
          p: 4,
        }}
      >
        <PrefsGrid
          prefs={prefs}
          role={role}
          onRoleChange={setRole}
          onToggle={handleToggle}
          onCadenceChange={handleCadenceChange}
          slackConnected={delivery.slack}
          emailConnected={delivery.email}
          saving={updateMutation.isPending}
        />
        <QuietHours />
      </Box>

      <ChannelManageDrawer
        channelId={manageChannel}
        slack={slack}
        email={email}
        prefs={prefs[role]}
        roleId={role}
        canManage={canManage}
        preferencesPending={updateMutation.isPending}
        testPending={slackTest.isPending}
        disconnectPending={disconnectSlack.isPending}
        notice={slackSetup.notice}
        onToggle={handleDrawerToggle}
        onCadenceChange={handleDrawerCadence}
        onTest={sendSlackTest}
        onReconnect={() => {
          setManageChannel(null);
          void slackSetup.startSlackLink();
        }}
        onDisconnect={disconnectSlackWorkspace}
        onClose={() => setManageChannel(null)}
      />

      <SlackSetupDialog
        open={slackSetup.setupStep !== null}
        step={slackSetup.setupStep ?? 'redirecting'}
        slack={{ ...slack, channel: slackSetup.configuredChannel || slack.channel }}
        onCancel={slackSetup.closeSetup}
        onReconnect={() => void slackSetup.startSlackLink()}
        onConfigured={(channel) => slackSetup.completeChannelConfiguration(channel.name)}
        onFinish={slackSetup.finishSetup}
      />

      <SlackSetupSuccessToast
        channel={slackSetup.successChannel}
        onClose={slackSetup.dismissSuccess}
      />
    </Stack>
  );
}

export const Route = createFileRoute('/settings/notifications/')({
  validateSearch: (search) => notificationsSearchSchema.parse(search),
  component: NotificationsPage,
});
